import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { FlowJSON } from '@/lib/whatsapp-flows';

/**
 * useFlowDefinition - V1205
 *
 * Fetches WhatsApp Flow definition (flow_json) from whatsapp_templates table.
 * Used to render flow screens in the chat conversation view.
 *
 * @param flowId - Meta flow ID (template_id in our database)
 * @param options - Query options
 */

interface FlowDefinition {
  id: string;
  template_name: string;
  template_id: string;
  flow_json: FlowJSON | null;
  status: string;
}

interface UseFlowDefinitionOptions {
  enabled?: boolean;
}

export function useFlowDefinition(
  flowId: string | null | undefined,
  options: UseFlowDefinitionOptions = {}
) {
  const { tenantData } = useAuth();
  const { enabled = true } = options;

  return useQuery<FlowDefinition | null>({
    queryKey: ['flow-definition', flowId, tenantData?.schema_name],
    queryFn: async () => {
      if (!flowId || !tenantData?.schema_name) {
        return null;
      }

      // Query whatsapp_templates table for the flow definition
      const { data, error } = await supabase
        .from('whatsapp_templates')
        .select('id, template_name, template_id, flow_json, status')
        .eq('template_id', flowId)
        .eq('type', 'flow')
        .maybeSingle();

      if (error) {
        console.error('[useFlowDefinition] Error fetching flow:', error);
        throw error;
      }

      if (!data) {
        console.warn(`[useFlowDefinition] Flow not found: ${flowId}`);
        return null;
      }

      return {
        id: data.id,
        template_name: data.template_name,
        template_id: data.template_id,
        flow_json: data.flow_json as FlowJSON | null,
        status: data.status || 'UNKNOWN',
      };
    },
    enabled: enabled && !!flowId && !!tenantData?.schema_name,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    retry: 1,
  });
}

/**
 * Extract flow_id from nfm_reply message metadata
 *
 * The flow_id can be in several places depending on how the flow was configured:
 * 1. metadata.flow_id - Direct flow ID
 * 2. metadata.flow_token - Contains encoded flow_id
 * 3. Parse from flow_token or response
 */
export function extractFlowIdFromMessage(message: {
  metadata?: {
    flow_id?: string;
    flow_token?: string;
    flow_response?: Record<string, unknown>;
    nfm_reply?: {
      response_json?: string;
    };
  } | null;
  content?: string;
}): string | null {
  // Direct flow_id in metadata
  if (message.metadata?.flow_id) {
    return message.metadata.flow_id;
  }

  // Try to extract from flow_response
  const flowResponse = message.metadata?.flow_response || tryParseContent(message.content);
  if (flowResponse?.flow_id) {
    return flowResponse.flow_id;
  }

  // flow_token might contain flow_id (format varies)
  // This is a fallback - flow_token is usually a session token, not flow_id
  // We may need to query by other means

  return null;
}

function tryParseContent(content?: string): Record<string, unknown> | null {
  if (!content) return null;
  try {
    return JSON.parse(content);
  } catch {
    return null;
  }
}

export default useFlowDefinition;
